import { Fragment, useCallback } from 'react';
import exit from '~/assets/icons/exit.svg';
import wallet from '~/assets/icons/wallet.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Divider } from '~/components/ui/Divider';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import {
	Currency,
	IModalReplyWithValue,
	ModalButtonColor,
	ModalType,
	numWord,
	WalletType,
} from '~/shared';
import homePreview from '../../assets/images/home_preview.png';
import {
	setActiveMenu,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Head } from '../Head';
import {
	StyledButtonContent,
	StyledBuyContainer,
	StyledHomeImage,
	StyledInfoContainer,
	StyledInfoItem,
	StyledInfoItemsContainers,
	StyledNotOwnerContainer,
	StyledOwnerContainer,
	StyledRubleContainer,
} from './HomeMenu.styled';

const HomeMenu: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const houseData = useLayoutSelector((state) => state.homeBuying.houseData);
	const modalState = useObservable(rage.modal.getCurrentModal());

	const rubleIcon = useWalletIcon(Currency.Ruble, true);

	const handleBuyGov = useCallback(() => {
		if (!houseData || modalState) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				text: `Вы действительно хотите купить дом #${houseData.id}?`,
				price: houseData?.buyPrice || 0,
				walletTypes: [WalletType.Cash, WalletType.Bank],
				acceptButton: { text: 'Купить', color: ModalButtonColor.Success },
				cancelButton: {
					text: 'Отменить',
					color: ModalButtonColor.Secondary,
				},
			},
			(result: IModalReplyWithValue<WalletType | null>) => {
				if (result.isAccepted) {
					rage.houses
						.buy(houseData.id, result.value ?? WalletType.Cash)
						.catch((err) => rage.notifications.showException(err));
				}
			},
		);
	}, [houseData, modalState]);

	return (
		<div>
			<Head />
			<Divider
				$direction="H"
				style={{ marginTop: 20, marginBottom: 30, opacity: 0.1 }}
			/>
			<StyledHomeImage src={homePreview} />
			<StyledInfoItemsContainers>
				<StyledInfoItem>
					<Text>Класс</Text>
					<Text style={{ color: houseData?.houseClassColor }}>
						{houseData?.houseClassName}
					</Text>
				</StyledInfoItem>
				<StyledInfoItem>
					<Text>Гараж</Text>
					<Text>
						{houseData?.garagePlaces}{' '}
						{numWord(houseData?.garagePlaces ?? 0, ['место', 'места', 'мест'])}
					</Text>
				</StyledInfoItem>
				<StyledInfoItem>
					<Text>Гос. стоимость</Text>
					<Text>{houseData?.buyPrice.toLocaleString('ru-RU')} ₽</Text>
				</StyledInfoItem>
			</StyledInfoItemsContainers>
			<StyledInfoContainer>
				<Text>Замок</Text>
				<Text style={{ fontWeight: 500 }}>
					{houseData?.isLocked ? 'Закрытый' : 'Открытый'}
				</Text>
				<Text>Владелец</Text>
				<Text style={{ fontWeight: 500 }}>
					{houseData?.ownerId ? houseData?.ownerNickname : 'На продаже'}
				</Text>
				<Text>Обслуживание</Text>
				<Text style={{ fontWeight: 500 }}>
					{houseData?.dayPrice.toLocaleString('ru-RU')}{' '}
					<span style={{ opacity: 0.5, fontWeight: 400 }}>₽/д.</span>
				</Text>
				<Text>Оплачено дней</Text>
				{houseData?.isOwned ? (
					<Text style={{ fontWeight: 500 }}>
						{houseData?.daysPaid}{' '}
						<span style={{ opacity: 0.5, fontWeight: 400 }}>
							/ {houseData?.daysMax} д.
						</span>
					</Text>
				) : (
					<Text style={{ opacity: 0.5, fontWeight: 500 }}>--</Text>
				)}
				{!houseData?.ownerId && (
					<Fragment>
						<Divider
							$direction="H"
							style={{ opacity: 0.1, gridColumn: 'span 2' }}
						/>
						<Text style={{ justifySelf: 'start', opacity: 0.5 }}>
							Стоимость
						</Text>
						<StyledRubleContainer>
							<Text>{houseData?.buyPrice.toLocaleString('ru-RU')}</Text>
							<CurrencyIcon icon={rubleIcon} size="sm" />
						</StyledRubleContainer>
					</Fragment>
				)}
			</StyledInfoContainer>
			{!houseData?.ownerId && (
				<StyledBuyContainer>
					<Button
						color={ButtonColors.Default}
						height={ButtonHeight.Height50}
						onClick={() =>
							rage.houses
								.exitMenu()
								.catch((err) => rage.notifications.showException(err))
						}
					>
						<Text>Выйти</Text>
					</Button>
					<Button
						color={ButtonColors.Buy}
						height={ButtonHeight.Height50}
						onClick={(e) => {
							e.currentTarget.blur();
							handleBuyGov();
						}}
					>
						<StyledButtonContent>
							<img src={wallet} width={20} height={20} />
							<Text>Купить</Text>
						</StyledButtonContent>
					</Button>
				</StyledBuyContainer>
			)}
			{houseData?.ownerId && !houseData.isOwned && (
				<StyledNotOwnerContainer>
					<Button
						color={ButtonColors.Select2}
						height={ButtonHeight.Height50}
						disabled={houseData.isLocked}
						onClick={() => rage.houses.enterHouse()}
					>
						{houseData.isLocked ? (
							<Text>Дом закрыт</Text>
						) : (
							<StyledButtonContent>
								<img src={exit} width={20} height={20} />
								<Text>Войти в дом</Text>
							</StyledButtonContent>
						)}
					</Button>
				</StyledNotOwnerContainer>
			)}
			{houseData?.ownerId && houseData.isOwned && (
				<StyledOwnerContainer>
					<Button
						color={ButtonColors.Buy}
						height={ButtonHeight.Height50}
						onClick={() => dispatch(setActiveMenu('sell'))}
					>
						<Text>Продать недвижимость</Text>
					</Button>
					<Button
						color={ButtonColors.Select2}
						height={ButtonHeight.Height50}
						onClick={() => rage.houses.enterHouse()}
					>
						<StyledButtonContent>
							<img src={exit} width={20} height={20} />
							<Text>Войти в дом</Text>
						</StyledButtonContent>
					</Button>
				</StyledOwnerContainer>
			)}
		</div>
	);
};

export { HomeMenu };
